import ctl from '@netlify/classnames-template-literals'
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import { Select } from 'radix-ui'
import { JSX, memo, ReactNode, useMemo, useState } from 'react'

export type WidgetInputQuickSwitchOption<T extends string> = {
	id: T
	// use the `inList` prop to show extra information when in the list.
	render: (inList: boolean) => ReactNode
}

export const widgetInputQuickSwitchDivider = {
	id: 'divider',
	render: () => <></>, // use SelectSeparator instead
}

export type WidgetInputQuickSwitchProps<T extends string> = {
	options: ReadonlyArray<WidgetInputQuickSwitchOption<T> | typeof widgetInputQuickSwitchDivider>
	value: T
	onChange: (value: T) => void
	label: string
	placeholder: string
}

export const WidgetInputQuickSwitch = memo(function WidgetInputQuickSwitch<T extends string>({
	options,
	value,
	onChange,
	label,
	placeholder,
}: WidgetInputQuickSwitchProps<T>) {
	const [open, setOpen] = useState(false)

	// Ensure we always have a controlled value by providing a fallback
	const controlledValue = useMemo(() => value || options[0]?.id || '', [value, options])

	// Memoize the current option to avoid repeated finds
	const currentOption = useMemo(() => options.find((v) => v.id === value), [options, value])

	return (
		<div className="grid gap-1">
			<Select.Root value={controlledValue} onValueChange={onChange}>
				<Select.Trigger
					className={ctl(`
						inline-flex
                        items-center justify-between gap-2
                        text-sm text-grey-900
                        focus:outline-none
                        relative
						cursor-pointer
						group/quick-switch
					`)}
					aria-label={label ?? ''}
				>
					<Select.Value placeholder={placeholder}>
						{currentOption?.render(false)}
					</Select.Value>
					{
						/* <Select.Icon className="ml-1 text-grey-500">
						<ChevronDownIcon className="size-4" />
					</Select.Icon> */
					}
				</Select.Trigger>
				<Select.Portal>
					<Select.Content className="z-50 overflow-hidden rounded-sm border border-grey-200 bg-white shadow-lg">
						<Select.ScrollUpButton className="flex h-6 cursor-default items-center justify-center bg-white text-grey-600">
							<ChevronUpIcon className="size-4" />
						</Select.ScrollUpButton>
						<Select.Viewport className="grid gap-1 p-1">
							<div className="px-2 py-1.5 text-xs font-medium text-grey-500">
								{label}
							</div>
							{options.map((prop, i) => {
								if (prop.id === widgetInputQuickSwitchDivider.id) {
									return <SelectSeparator key={i} />
								}
								return (
									<SelectItem key={prop.id} value={prop.id}>
										{prop.render(true)}
									</SelectItem>
								)
							})}
						</Select.Viewport>
						<Select.ScrollDownButton className="flex h-6 cursor-default items-center justify-center bg-white text-grey-600">
							<ChevronDownIcon className="size-4" />
						</Select.ScrollDownButton>
					</Select.Content>
				</Select.Portal>
			</Select.Root>
		</div>
	)
}) as <T extends string>(props: WidgetInputQuickSwitchProps<T>) => JSX.Element

const SelectItem = memo(function SelectItem({
	children,
	className,
	value,
}: {
	children: ReactNode
	className?: string
	value: string
}) {
	return (
		<Select.Item
			className={ctl(`
				relative w-full select-none rounded-sm text-sm outline-none
                cursor-pointer
				data-[disabled]:pointer-events-none data-[disabled]:opacity-50
				data-[highlighted]:bg-purple-50 data-[highlighted]:text-purple-700
				${className ?? ''}
			`)}
			value={value}
		>
			<Select.ItemText>{children}</Select.ItemText>
			{
				/* <Select.ItemIndicator className="absolute right-2 inline-flex items-center">
				<CheckIcon className="size-4" />
			</Select.ItemIndicator> */
			}
		</Select.Item>
	)
})

const SelectItemGroup = memo(function SelectItemGroup<T extends string>({
	children,
	className,
	label,
}: {
	children: ReactNode
	className?: string
	label: string
}) {
	return (
		<Select.Group>
			<Select.Label className="px-2 py-1.5 text-xs font-medium text-grey-500">
				{label}
			</Select.Label>
			{children}
		</Select.Group>
	)
})

const SelectSeparator = memo(function SelectSeparator() {
	return <Select.Separator className="my-1.5 mx-2 h-px bg-grey-100" />
})
